import { useEffect, useState } from "react";
import { dashboardService } from "../../services/api";
import { fmtDateTimezone } from "../utils/dateUtils/fmtDate";
import StatCard from "../../components/StatCard/StatCard";
import StatusPill from "../../components/StatusPill/StatusPill";
import { useNotification } from "../../components/Notifications/NotificationContext";
import { CollapsibleMetrics } from '../../components/MetricsToggle/MetricsToggle';

const STATUS_COLORS = {
    pending: "yellow",
    reviewing: "blue",
    resolved: "green",
    rejected: "red",
};

const STATUS_LABELS = {
    pending: "Pendente",
    reviewing: "Em análise",
    resolved: "Resolvida",
    rejected: "Rejeitada",
};

export default function AdminReports() {
    const [reports, setReports] = useState([]);
    const [statusCounts, setStatusCounts] = useState({});
    const [loading, setLoading] = useState(false);
    const { showError, showSuccess } = useNotification();
    const [filters, setFilters] = useState({ status: "", bot_username: "" });
    const [pagination, setPagination] = useState({});
    const [currentPage, setCurrentPage] = useState(1);

    useEffect(() => {
        loadReports(1);
    }, []);

    const loadReports = async (page = 1, override = null) => {
        const f = override || filters;
        try {
            setLoading(true);
            const { data } = await dashboardService.getAdminReports({ page, ...f });
            setReports(data.reports || []);
            setStatusCounts(data.status_counts || {});
            setPagination(data.pagination || {});
            setCurrentPage(page);
        } catch (err) {
            console.error(err);
            showError("Erro ao carregar denúncias.");
        } finally {
            setLoading(false);
        }
    };

    const handleStatus = async (report, newStatus) => {
        try {
            await dashboardService.updateReportStatus(report.id, { status: newStatus });
            showSuccess(`Denúncia #${report.id}: ${STATUS_LABELS[newStatus]}.`);
            loadReports(currentPage);
        } catch (err) {
            console.error(err);
            showError("Erro ao atualizar. " + (err?.response?.data?.error || ""));
        }
    };

    return (
        <>
            {/* CARDS RESUMO */}
            <CollapsibleMetrics>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <StatCard title="Pendentes" value={statusCounts.pending || 0} icon={{ class: "fas fa-hourglass-half", color: "red" }} />
                    <StatCard title="Em análise" value={statusCounts.reviewing || 0} icon={{ class: "fas fa-magnifying-glass", color: "blue" }} />
                    <StatCard title="Resolvidas" value={statusCounts.resolved || 0} icon={{ class: "fas fa-check-circle", color: "green" }} />
                    <StatCard title="Rejeitadas" value={statusCounts.rejected || 0} icon={{ class: "fas fa-times-circle", color: "purple" }} />
                </div>
            </CollapsibleMetrics>

            {/* FILTROS */}
            <div className="filters-section mt-4">
                <div className="filters-grid">
                    <div className="filter-group">
                        <input
                            type="text"
                            placeholder="@ do bot denunciado"
                            value={filters.bot_username}
                            onChange={(e) => setFilters((p) => ({ ...p, bot_username: e.target.value }))}
                            className="filter-input"
                        />
                    </div>
                    <div className="filter-group">
                        <select
                            className="filter-input"
                            value={filters.status}
                            onChange={(e) => {
                                const next = { ...filters, status: e.target.value };
                                setFilters(next);
                                loadReports(1, next);
                            }}
                        >
                            <option value="">Todos os status</option>
                            {Object.entries(STATUS_LABELS).map(([v, l]) => (
                                <option key={v} value={v}>{l}</option>
                            ))}
                        </select>
                    </div>
                    <button onClick={() => loadReports(1)} className="btn-primary">
                        Aplicar Filtros
                    </button>
                </div>
            </div>

            {/* CARDS DE DENÚNCIAS */}
            <div className="admin-cards">
                {reports.map((r) => (
                    <div key={r.id} className="admin-card">
                        <div className="admin-card-head">
                            <div className="admin-avatar"><i className="fas fa-flag" /></div>
                            <div className="admin-card-id">
                                <div className="admin-card-name-row">
                                    <span className="admin-card-name">@{r.reported_bot_username}</span>
                                    <span className="admin-chip admin-chip--danger">
                                        {r.report_type_label}
                                    </span>
                                </div>
                                <span className="admin-card-sub">
                                    <i className="fas fa-user" /> Denunciado por: {r.reporter_username} ({r.reporter_email})
                                </span>
                                <div className="admin-bot-dates">
                                    <span><i className="fas fa-calendar" /> {fmtDateTimezone(r.created_at)}</span>
                                    {r.platform && <span><i className="fas fa-globe" /> {r.platform_label}</span>}
                                    {r.reviewed_at && <span><i className="fas fa-gavel" /> Analisada: {fmtDateTimezone(r.reviewed_at)}</span>}
                                </div>
                            </div>
                            <StatusPill color={STATUS_COLORS[r.status] || "gray"}>
                                {r.status_label}
                            </StatusPill>
                        </div>

                        <div className="admin-copy-preview">{r.description}</div>

                        {(r.redirect_link || r.content_link || r.pix_code) && (
                            <div className="admin-bot-dates" style={{ flexDirection: "column", gap: 6 }}>
                                {r.redirect_link && (
                                    <span style={{ overflowWrap: "anywhere" }}>
                                        <i className="fas fa-random" /> Redirecionador: {r.redirect_link}
                                    </span>
                                )}
                                {r.content_link && (
                                    <span style={{ overflowWrap: "anywhere" }}>
                                        <i className="fas fa-link" /> Conteúdo: {r.content_link}
                                    </span>
                                )}
                                {r.pix_code && (
                                    <span style={{ overflowWrap: "anywhere" }}>
                                        <i className="fas fa-qrcode" /> PIX: {r.pix_code}
                                    </span>
                                )}
                            </div>
                        )}

                        <div className="admin-card-footer">
                            {r.status === "pending" && (
                                <button type="button" className="admin-icon-btn is-blue" onClick={() => handleStatus(r, "reviewing")}>
                                    <i className="fas fa-magnifying-glass" /><span>Analisar</span>
                                </button>
                            )}
                            {r.status !== "resolved" && (
                                <button type="button" className="admin-icon-btn is-success" onClick={() => handleStatus(r, "resolved")}>
                                    <i className="fas fa-check" /><span>Resolver</span>
                                </button>
                            )}
                            {r.status !== "rejected" && (
                                <button type="button" className="admin-icon-btn is-danger" onClick={() => handleStatus(r, "rejected")}>
                                    <i className="fas fa-times" /><span>Rejeitar</span>
                                </button>
                            )}
                        </div>
                    </div>
                ))}
            </div>

            {reports.length === 0 && !loading && (
                <div className="empty-state">
                    <i className="fas fa-flag" />
                    <h4>Nenhuma denúncia encontrada</h4>
                </div>
            )}

            {/* PAGINAÇÃO */}
            {!loading && reports.length > 0 && (
                <div className="mt-8 flex items-center justify-between">
                    <div className="text-sm text-gray-600">
                        Página {currentPage} de {pagination.pages} ({pagination.total} denúncias)
                    </div>
                    <div className="flex gap-2">
                        <button
                            onClick={() => loadReports(currentPage - 1)}
                            disabled={!pagination.has_prev}
                            className="btn-secondary"
                        >
                            <i className="fas fa-chevron-left"></i> Anterior
                        </button>
                        <button
                            onClick={() => loadReports(currentPage + 1)}
                            disabled={!pagination.has_next}
                            className="btn-primary"
                        >
                            Próximo <i className="fas fa-chevron-right"></i>
                        </button>
                    </div>
                </div>
            )}
        </>
    );
}
